// client/src/coms/components/comslayout_parts/ComsEmptyState.js
import React from 'react';
import CreateServerModal from '../CreateServerModal';

export default function ComsEmptyState({
  servers,
  serversLoaded,
  apiAvailable,
  authState,
  lastApiError,
  joinInviteCode,
  setJoinInviteCode,
  joinBusy,
  joinErr,
  openJoinOnEnter,
  handleJoinServer,
  createServerOpen,
  setCreateServerOpen,
  handleCreateServer,
  refreshServers,
  navigate,
}) {
  const buildPanel = (title, body, actions, extra) => {
    return (
      <div className="coms-shell">
        <div className="coms-main" style={{ padding: 18 }}>
          <div
            style={{
              maxWidth: 860,
              margin: '0 auto',
              padding: 20,
              borderRadius: 18,
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
              boxShadow: '0 18px 60px rgba(0,0,0,0.30)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 14,
                  background: 'rgba(var(--vx-link-rgb, 140,170,255), 0.12)',
                  border: '1px solid rgba(var(--vx-link-rgb, 140,170,255), 0.22)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 900,
                  color: 'rgba(200,215,255,0.95)',
                }}
              >
                !
              </div>
              <div style={{ fontSize: 22, fontWeight: 900 }}>{title}</div>
            </div>

            <div style={{ opacity: 0.88, lineHeight: 1.55, marginBottom: 14, fontSize: 14 }}>{body}</div>

            {extra ? <div style={{ marginBottom: 14 }}>{extra}</div> : null}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</div>
          </div>
        </div>

        {createServerOpen ? (
          <CreateServerModal onClose={() => setCreateServerOpen(false)} onCreate={handleCreateServer} />
        ) : null}
      </div>
    );
  };

  if (!serversLoaded) return <div className="coms-shell">Loading…</div>;

  if (authState && authState.status === 'auth') {
    return buildPanel(
      'Sign in required',
      'You are not authenticated, so Comms cannot load your servers. Sign in, then return to Comms.',
      <>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.92 * var(--vx-ov, 1)))',
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          Go to homepage
        </button>
        <button
          type="button"
          onClick={() => refreshServers(true)}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-link-rgb, 140,170,255), 0.30)',
            background: 'rgba(var(--vx-link-rgb, 140,170,255), 0.12)',
            color: 'rgba(235,240,255,0.95)',
            fontWeight: 900,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </>
    );
  }

  if (authState && authState.status === 'forbidden') {
    return buildPanel(
      'Access blocked',
      'Your account is authenticated, but access to Comms is forbidden.',
      <>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.92 * var(--vx-ov, 1)))',
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          Go to homepage
        </button>
      </>
    );
  }

  if (apiAvailable === false) {
    const detailBox = lastApiError ? (
      <div
        style={{
          padding: 12,
          borderRadius: 14,
          background: 'rgba(0,0,0,0.22)',
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
          fontFamily: `ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace`,
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          opacity: 0.92,
        }}
      >
        {lastApiError}
      </div>
    ) : null;

    return buildPanel(
      'Comms is temporarily unavailable',
      'The Comms API didn’t respond, so no servers can be shown. This is not a fallback server state — it’s a hard “no data” state until the API is reachable again.',
      <>
        <button
          type="button"
          onClick={() => refreshServers(true)}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-link-rgb, 140,170,255), 0.30)',
            background: 'rgba(var(--vx-link-rgb, 140,170,255), 0.12)',
            color: 'rgba(235,240,255,0.95)',
            fontWeight: 900,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>

        <button
          type="button"
          onClick={() => navigate('/comms')}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.92 * var(--vx-ov, 1)))',
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          Back to Comms
        </button>

        <button
          type="button"
          onClick={() => setCreateServerOpen(true)}
          disabled
          title="Disabled while the Comms API is unavailable"
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.45 * var(--vx-ov, 1)))',
            fontWeight: 900,
            cursor: 'not-allowed',
          }}
        >
          Create server
        </button>
      </>,
      detailBox
    );
  }

  if (Array.isArray(servers) && servers.length === 0) {
    const joinBox = (
      <div
        style={{
          padding: 14,
          borderRadius: 16,
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
          background: 'rgba(0,0,0,0.18)',
        }}
      >
        <div
          style={{
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            opacity: 0.7,
            fontWeight: 900,
          }}
        >
          Join server
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 12, opacity: 0.75, fontWeight: 800 }}>Invite code</div>
            <input
              value={joinInviteCode}
              onChange={(e) => setJoinInviteCode(e.target.value)}
              onKeyDown={openJoinOnEnter}
              placeholder="invite code"
              autoComplete="off"
              spellCheck={false}
              style={{
                height: 38,
                borderRadius: 12,
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.92 * var(--vx-ov, 1)))',
                padding: '0 12px',
                outline: 'none',
                fontSize: 13,
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleJoinServer}
            disabled={joinBusy || !String(joinInviteCode || '').trim()}
            style={{
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid rgba(var(--vx-link-rgb, 140,170,255), 0.30)',
              background: 'rgba(var(--vx-link-rgb, 140,170,255), 0.12)',
              color: 'rgba(235,240,255,0.95)',
              fontWeight: 900,
              cursor: joinBusy || !String(joinInviteCode || '').trim() ? 'not-allowed' : 'pointer',
              opacity: joinBusy || !String(joinInviteCode || '').trim() ? 0.55 : 1,
            }}
          >
            {joinBusy ? 'Joining…' : 'Join'}
          </button>

          {joinErr ? (
            <div style={{ fontSize: 12, color: '#ff6b6b', fontWeight: 800, opacity: 0.95 }}>{joinErr}</div>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.55 }}>Enter the invite code from the owner.</div>
          )}
        </div>
      </div>
    );

    return buildPanel(
      'No servers yet',
      'Create your first server to start voice and text channels, or join an existing one using an invite.',
      <>
        <button
          type="button"
          onClick={() => setCreateServerOpen(true)}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-link-rgb, 140,170,255), 0.30)',
            background: 'rgba(var(--vx-link-rgb, 140,170,255), 0.12)',
            color: 'rgba(235,240,255,0.95)',
            fontWeight: 900,
            cursor: 'pointer',
          }}
        >
          Create server
        </button>

        <button
          type="button"
          onClick={() => refreshServers(true)}
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.92 * var(--vx-ov, 1)))',
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </>,
      joinBox
    );
  }

  return <div className="coms-shell">Loading…</div>;
}